// Cross-section gate helpers for property-parts readiness (pre-calc / expenses
// locks). Lives beside the table helpers but imports only `shared/*` plus
// `getOptionalPartNameValidationError` + `buildPropertyPartGroups` from
// `haklaiPropertyPartsHelpers` so `preCalc/logic/haklaiPreCalcValidation.js` does not
// pull the full helpers module (which depends on expenses normalization).

import { CATEGORIES } from "../../shared/haklaiConstants";
import {
  getHaklaiPropertyDepreciationStatus,
  getHaklaiResidentialRentalProfile,
  hasHaklaiManualForm11DepreciationInput,
  HAKLAI_PROPERTY_DEPRECIATION_MODE,
  isHaklaiManualPurchaseDepreciationAllowed
} from "../../shared/haklaiDepreciationHelpers";
import {
  buildSortedPropertyPartRows,
  getHaklaiComputedAcquisitionByPart,
  getHaklaiPartDisplayLabel,
  hasPositiveNumericValue,
  isHaklaiBuiltValueRelevant,
  isIgnorableTrailingPropertyPartRow,
  isOptionalPartCategory
} from "../../shared/haklaiFlowHelpers";
import {
  buildPropertyPartGroups,
  getOptionalPartNameValidationError
} from "./haklaiPropertyPartsHelpers";
import { HAKLAI_PROPERTY_PART_ERRORS } from "../haklaiPropertyPartsTexts";

export const getPropertyPartsValidationError = ({
  preCalc = {},
  propertyPartsData = {},
  totalPropertyCost
}) => {
  const residentialRows = buildSortedPropertyPartRows(propertyPartsData, CATEGORIES.RESIDENTIAL);
  const commercialRows = buildSortedPropertyPartRows(propertyPartsData, CATEGORIES.COMMERCIAL);
  const commercialBgRows = buildSortedPropertyPartRows(propertyPartsData, CATEGORIES.COMMERCIAL_BG);
  const landRows = buildSortedPropertyPartRows(propertyPartsData, CATEGORIES.LAND_AND_SHARED);
  const allRows = [...residentialRows, ...commercialRows, ...landRows, ...commercialBgRows];

  if (!allRows.length) return HAKLAI_PROPERTY_PART_ERRORS.missingAnyPart;

  const partNamesErr = getOptionalPartNameValidationError(propertyPartsData);
  if (partNamesErr) return partNamesErr;

  const effectiveEntries = [residentialRows, commercialRows, landRows, commercialBgRows]
    .flatMap((rows) => rows.filter((entry, index) => !isIgnorableTrailingPropertyPartRow(rows, index)));
  if (!effectiveEntries.length) return HAKLAI_PROPERTY_PART_ERRORS.missingAnyPart;

  const primaryResidence = residentialRows.find(([rowKey]) => rowKey === "residence_1")?.[1];
  if (!hasPositiveNumericValue(primaryResidence?.value)) {
    return HAKLAI_PROPERTY_PART_ERRORS.missingPrimaryResidenceValue;
  }

  let sum = 0;
  for (const [, part] of effectiveEntries) {
    const partValue = Number(part.value);
    if (!hasPositiveNumericValue(partValue)) return HAKLAI_PROPERTY_PART_ERRORS.missingPartValue;

    if (isOptionalPartCategory(part.category) && !String(part.label || "").trim()) {
      return HAKLAI_PROPERTY_PART_ERRORS.missingAddedPartName;
    }

    if (part.category === CATEGORIES.RESIDENTIAL) {
      const hasBuiltValueRaw =
        part.valueWithoutZaban !== null
        && part.valueWithoutZaban !== undefined
        && part.valueWithoutZaban !== "";
      const requiresBuiltValue = preCalc.hasZaban === true && isHaklaiBuiltValueRelevant(part, true);
      if (requiresBuiltValue && !hasBuiltValueRaw) {
        return HAKLAI_PROPERTY_PART_ERRORS.missingRelevantBuiltValue;
      }
      if (!requiresBuiltValue || !hasBuiltValueRaw) { sum += partValue; continue; }

      const builtValue = Number(part.valueWithoutZaban);
      if (!hasPositiveNumericValue(builtValue)) return HAKLAI_PROPERTY_PART_ERRORS.invalidRelevantBuiltValue;
      if (builtValue > partValue) return HAKLAI_PROPERTY_PART_ERRORS.builtValueGreaterThanPartValue;
    }

    sum += partValue;
  }

  if (hasPositiveNumericValue(totalPropertyCost) && sum !== Number(totalPropertyCost)) {
    return HAKLAI_PROPERTY_PART_ERRORS.saleValueTotalMismatch;
  }

  return null;
};

export const getHaklaiPropertyPartsDepreciationGaps = ({
  propertyPartsData = {},
  residentialRentalByPart = {},
  purchaseDepreciationByPart = {},
  soldPropertyDate = "",
  ownershipStartDate = "",
  purchaseDate = "",
  totalAcquisitionCost = 0
} = {}) => {
  const groups = buildPropertyPartGroups(propertyPartsData);
  const computedAcquisitionByPart = getHaklaiComputedAcquisitionByPart(
    propertyPartsData,
    totalAcquisitionCost
  );

  return groups.renderableRows.reduce((gaps, entry) => {
    const [rowKey, part] = entry;
    const residentialRentalProfile = part?.category === CATEGORIES.RESIDENTIAL
      ? getHaklaiResidentialRentalProfile({
        partKey: rowKey,
        part,
        residentialRentalByPart,
        purchaseDepreciationByPart
      })
      : {};
    const rawManualProfile = purchaseDepreciationByPart[rowKey];
    const manualProfile = hasHaklaiManualForm11DepreciationInput(rawManualProfile)
      ? rawManualProfile
      : {};
    const checks = [];
    if (Object.keys(residentialRentalProfile).length > 0) {
      checks.push({
        depreciation: residentialRentalProfile,
        mode: HAKLAI_PROPERTY_DEPRECIATION_MODE.RESIDENTIAL_AUTO
      });
    }
    if (
      Object.keys(manualProfile).length > 0
      && isHaklaiManualPurchaseDepreciationAllowed(part)
    ) {
      checks.push({
        depreciation: manualProfile,
        mode: HAKLAI_PROPERTY_DEPRECIATION_MODE.MANUAL_FORM_11
      });
    }
    if (!checks.length) return gaps;

    const status = checks
      .map((check) => getHaklaiPropertyDepreciationStatus({
        part,
        depreciation: check.depreciation,
        soldPropertyDate,
        ownershipStartDate,
        purchaseDate,
        computedAcquisitionAmount: computedAcquisitionByPart[rowKey] || 0,
        modeOverride: check.mode
      }))
      .find((candidateStatus) => candidateStatus === "partial")
      || "ready";

    // Missing depreciation is now allowed; only malformed / partial
    // depreciation data should block the downstream flow.
    if (status === "notRelevant" || status === "notConfigured" || status === "blocked" || status === "ready") {
      return gaps;
    }

    const displayLabel = getHaklaiPartDisplayLabel(
      entry,
      groups.rowsByCategory[part?.category] || []
    );

    gaps.push({
      rowKey,
      status,
      displayLabel: displayLabel || String(part?.label || "").trim()
    });
    return gaps;
  }, []);
};
